import { useKeyboardControls } from '@react-three/drei'
import { addEffect } from '@react-three/fiber'
import { useEffect, useRef, useState } from 'react'
import useGame from './stores/useGame'
import { useBackgroundMusic } from './hooks/useMusic'

export default function Interface()
{
    const phase = useGame((state) => state.phase)
    const { playMusic } = useBackgroundMusic()
    const [musicStarted, setMusicStarted] = useState(false)
    const restart = useGame((state) => state.restart)
    const backToMenu = useGame((state) => state.backToMenu)
    const selectLevel = useGame((state) => state.selectLevel)
    const currentLevel = useGame((state) => state.currentLevel)
    const levelSettings = useGame((state) => state.levelSettings)
    const crystals = useGame((state) => state.crystals)
    const totalCrystals = useGame((state) => state.totalCrystals)
    const bestTimes = useGame((state) => state.bestTimes)
    const ballSkins = useGame((state) => state.ballSkins)
    const selectedBall = useGame((state) => state.selectedBall)
    const selectBall = useGame((state) => state.selectBall)

    const time = useRef()

    const forward = useKeyboardControls((state) => state.forward)
    const backward = useKeyboardControls((state) => state.backward)
    const leftward = useKeyboardControls((state) => state.leftward)
    const rightward = useKeyboardControls((state) => state.rightward)
    const jump = useKeyboardControls((state) => state.jump)

    useEffect(() => {
        const unsubscribeEffect = addEffect(() => {
            const state = useGame.getState()

            let elapsedTime = 0

            if (state.phase === 'playing') {
                elapsedTime = Date.now() - state.startTime
            } else if (state.phase === 'ended' || state.phase === 'failed') {
                elapsedTime = state.endTime ? state.endTime - state.startTime : Date.now() - state.startTime
            }

            elapsedTime /= 1000

            if (time.current) {
                time.current.textContent = elapsedTime.toFixed(1) + 's'
            }
        })

        return () => {
            unsubscribeEffect()
        }
    }, [])

    // Start music on first click anywhere
    useEffect(() => {
        if (musicStarted) return

        const handleFirstClick = () => {
            playMusic()
            setMusicStarted(true)
            document.removeEventListener('click', handleFirstClick)
            document.removeEventListener('keydown', handleFirstClick)
            document.removeEventListener('touchstart', handleFirstClick)
        }

        document.addEventListener('click', handleFirstClick)
        document.addEventListener('keydown', handleFirstClick)
        document.addEventListener('touchstart', handleFirstClick)

        return () => {
            document.removeEventListener('click', handleFirstClick)
            document.removeEventListener('keydown', handleFirstClick)
            document.removeEventListener('touchstart', handleFirstClick)
        }
    }, [musicStarted, playMusic])

    // MENU SCREEN - Butunlay yangi dizayn
    if (phase === 'menu') {
        return (
            <div className="interface">
                <div className="menu-screen">
                    <div className="menu-wrapper">
                        {/* Logo */}
                        <div className="logo-section">
                            <div className="logo-icon">❄️</div>
                            <h1 className="game-logo">ICE ROLL</h1>
                            <p className="game-tagline">Roll through the frozen world!</p>
                        </div>

                        {/* Ball Selector - Inline */}
                        <div className="ball-selector">
                            <span className="selector-label">Choose your ball:</span>
                            <div className="ball-options">
                                {ballSkins.map((ball) => (
                                    <button
                                        key={ball.id}
                                        className={`ball-option ${selectedBall.id === ball.id ? 'active' : ''}`}
                                        onClick={() => selectBall(ball)}
                                        title={ball.name}
                                        style={{ '--ball-color': ball.color }}
                                    >
                                        <span className="ball-emoji">{ball.icon}</span>
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Level Cards */}
                        <div className="level-cards">
                            {[1, 2, 3].map((level) => (
                                <button
                                    key={level}
                                    className={`level-card level-card-${level}`}
                                    onClick={() => selectLevel(level)}
                                >
                                    <div className="card-badge">{level}</div>
                                    <div className="card-content">
                                        <h3 className="card-title">{levelSettings[level].name}</h3>
                                        <div className="card-info">
                                            <span>{levelSettings[level].blocks} blocks</span>
                                            {levelSettings[level].dangers > 0 && (
                                                <span className="card-danger">
                                                    {levelSettings[level].dangers} traps
                                                </span>
                                            )}
                                        </div>
                                        {bestTimes[level] && (
                                            <div className="card-best">
                                                Best: {bestTimes[level].toFixed(1)}s
                                            </div>
                                        )}
                                    </div>
                                    <div className="card-play">PLAY</div>
                                </button>
                            ))}
                        </div>

                        {/* Controls hint */}
                        <div className="controls-hint-menu">
                            <span>WASD</span> to move <span>SPACE</span> to jump
                        </div>
                    </div>
                </div>
            </div>
        )
    }

    return (
        <div className="interface">
            {/* Game HUD */}
            <div className="game-hud">
                <div className="hud-pill hud-level">
                    <span className="pill-icon">🎯</span>
                    <span className="pill-text">{levelSettings[currentLevel].name}</span>
                </div>

                <div className="hud-timer" ref={time}>0.0s</div>

                <div className="hud-pill hud-crystals">
                    <span className="pill-icon">💎</span>
                    <span className="pill-text">{crystals}/{totalCrystals}</span>
                </div>
            </div>

            {/* READY Phase */}
            {phase === 'ready' && (
                <div className="ready-screen">
                    <div className="ready-badge">
                        <div className="ready-icon">🎮</div>
                        <h2 className="ready-title">READY?</h2>
                        <p className="ready-hint">Press any key to start!</p>
                    </div>
                </div>
            )}

            {/* SUCCESS Screen */}
            {phase === 'ended' && (
                <div className="result-screen success-screen">
                    <div className="result-card">
                        <div className="result-badge success-badge">
                            <span>🏆</span>
                        </div>
                        <h2 className="result-title">AWESOME!</h2>
                        <div className="result-stats">
                            <div className="stat-item">
                                <span className="stat-icon">⏱️</span>
                                <span className="stat-value">{time.current?.textContent}</span>
                            </div>
                            <div className="stat-item">
                                <span className="stat-icon">💎</span>
                                <span className="stat-value">{crystals}/{totalCrystals}</span>
                            </div>
                        </div>
                        <div className="result-actions">
                            <button className="action-btn primary-btn" onClick={restart}>
                                <span>🔄</span> Play Again
                            </button>
                            <button className="action-btn secondary-btn" onClick={backToMenu}>
                                <span>🏠</span> Menu
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* FAIL Screen */}
            {phase === 'failed' && (
                <div className="result-screen fail-screen">
                    <div className="result-card">
                        <div className="result-badge fail-badge">
                            <span>🗿</span>
                        </div>
                        <h2 className="result-title">OOPS!</h2>
                        <p className="fail-message">You fell into icy water!</p>
                        <div className="result-actions">
                            <button className="action-btn primary-btn" onClick={restart}>
                                <span>🔄</span> Try Again
                            </button>
                            <button className="action-btn secondary-btn" onClick={backToMenu}>
                                <span>🏠</span> Menu
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Controls indicator */}
            {phase === 'playing' && (
                <div className="play-controls">
                    <div className={`ctrl-key ${forward ? 'active' : ''}`}>W</div>
                    <div className="ctrl-row">
                        <div className={`ctrl-key ${leftward ? 'active' : ''}`}>A</div>
                        <div className={`ctrl-key ${backward ? 'active' : ''}`}>S</div>
                        <div className={`ctrl-key ${rightward ? 'active' : ''}`}>D</div>
                    </div>
                    <div className={`ctrl-key space-key ${jump ? 'active' : ''}`}>SPACE</div>
                </div>
            )}

            {/* Mobile Controls */}
            {(phase === 'ready' || phase === 'playing') && (
                <div className="mobile-controls">
                    <div className="joystick">
                        <button className={`joy-btn joy-up ${forward ? 'active' : ''}`}
                            onTouchStart={(e) => { e.preventDefault(); window.dispatchEvent(new KeyboardEvent('keydown', { code: 'KeyW' })) }}
                            onTouchEnd={() => window.dispatchEvent(new KeyboardEvent('keyup', { code: 'KeyW' }))}>
                            ▲
                        </button>
                        <div className="joy-middle">
                            <button className={`joy-btn joy-left ${leftward ? 'active' : ''}`}
                                onTouchStart={(e) => { e.preventDefault(); window.dispatchEvent(new KeyboardEvent('keydown', { code: 'KeyA' })) }}
                                onTouchEnd={() => window.dispatchEvent(new KeyboardEvent('keyup', { code: 'KeyA' }))}>
                                ◀
                            </button>
                            <div className="joy-center"></div>
                            <button className={`joy-btn joy-right ${rightward ? 'active' : ''}`}
                                onTouchStart={(e) => { e.preventDefault(); window.dispatchEvent(new KeyboardEvent('keydown', { code: 'KeyD' })) }}
                                onTouchEnd={() => window.dispatchEvent(new KeyboardEvent('keyup', { code: 'KeyD' }))}>
                                ▶
                            </button>
                        </div>
                        <button className={`joy-btn joy-down ${backward ? 'active' : ''}`}
                            onTouchStart={(e) => { e.preventDefault(); window.dispatchEvent(new KeyboardEvent('keydown', { code: 'KeyS' })) }}
                            onTouchEnd={() => window.dispatchEvent(new KeyboardEvent('keyup', { code: 'KeyS' }))}>
                            ▼
                        </button>
                    </div>
                    <button className={`jump-button ${jump ? 'active' : ''}`}
                        onTouchStart={(e) => { e.preventDefault(); window.dispatchEvent(new KeyboardEvent('keydown', { code: 'Space' })) }}
                        onTouchEnd={() => window.dispatchEvent(new KeyboardEvent('keyup', { code: 'Space' }))}>
                        JUMP
                    </button>
                </div>
            )}
        </div>
    )
}
